export interface CreateBatchLoaderOptions<K> {
  windowMs?: number
  maxBatchSize: number
  keyOf?: (key: K) => string
}

export interface BatchLoader<K, V> {
  load: (key: K) => Promise<V>
}

function defaultKeyOf<K>(key: K): string {
  if (typeof key === "string" || typeof key === "number" || typeof key === "boolean") {
    return String(key);
  }
  return JSON.stringify(key);
}

export function createBatchLoader<K, V>(
  fetchBatch: (keys: K[]) => Promise<Map<K, V>>,
  options: CreateBatchLoaderOptions<K>,
): BatchLoader<K, V> {
  const windowMs = options.windowMs ?? 20;
  const maxBatchSize = options.maxBatchSize;
  const keyOf = options.keyOf ?? defaultKeyOf<K>;

  interface Waiter {
    key: K
    resolve: (value: V) => void
    reject: (reason: unknown) => void
  }

  const pending: Waiter[] = [];
  let timer: ReturnType<typeof setTimeout> | null = null;

  function schedule(): void {
    if (pending.length >= maxBatchSize) {
      if (timer !== null) {
        clearTimeout(timer);
        timer = null;
      }
      flush();
      return;
    }
    if (timer === null) {
      timer = setTimeout(() => {
        timer = null;
        flush();
      }, windowMs);
    }
  }

  function flush(): void {
    if (pending.length === 0) {
      return;
    }
    const batch = pending.splice(0, maxBatchSize);
    if (pending.length > 0) {
      schedule();
    }
    void run(batch);
  }

  async function run(batch: Waiter[]): Promise<void> {
    const uniqueById = new Map<string, K>();
    const unique: K[] = [];
    for (const waiter of batch) {
      const id = keyOf(waiter.key);
      if (uniqueById.has(id)) {
        continue;
      }
      uniqueById.set(id, waiter.key);
      unique.push(waiter.key);
    }

    try {
      const result = await fetchBatch(unique);
      for (const waiter of batch) {
        const sourceKey = uniqueById.get(keyOf(waiter.key));
        if (sourceKey === undefined || !result.has(sourceKey)) {
          waiter.reject(new Error("createBatchLoader: missing key in batch result"));
          continue;
        }
        waiter.resolve(result.get(sourceKey) as V);
      }
    } catch (error) {
      for (const waiter of batch) {
        waiter.reject(error);
      }
    }
  }

  function load(key: K): Promise<V> {
    return new Promise((resolve, reject) => {
      pending.push({ key, resolve, reject });
      schedule();
    });
  }

  return { load };
}
